import { IconArrowRight, IconBooks, IconCalendarEvent, IconClockHour4, IconMapPin, IconPhone, IconSpeakerphone, IconUser, IconUsersGroup } from '@tabler/icons-react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';
import { PosterLogo, PosterSheet, fs, numberScale, posterFields, shopIsLong, valueFitScale } from '../posterParts';

/**
 * Landscape "Cards" — a coloured header strip with the logo and title, then four tall cards in a row (vacancies, qualification, age,
 * the two dates), a call-to-action bar and a shop strip. Palette: bg, hero, heroDeep, ink, muted, hot, cta, ctaDeep, cards (4 colours).
 */
const FONT = "'Baloo Da 2', 'Anek Bangla', 'Noto Sans Bengali', sans-serif";

function Card({ p, color, Icon, label, children }) {
    return (
        <div className="flex flex-col" style={{ background: '#fff', borderRadius: fs(2), borderTop: `${fs(1)} solid ${color}`, padding: `${fs(2.2)} ${fs(2.2)} ${fs(2.4)}`, gap: fs(1.1), boxShadow: '0 0.7cqw 1.7cqw rgba(15,23,42,0.12)' }}>
            <div className="flex items-center" style={{ gap: fs(1.4) }}>
                <div className="flex flex-none items-center justify-center rounded-full" style={{ width: fs(5.2), height: fs(5.2), background: color }}>
                    <Icon style={{ width: fs(3), height: fs(3), color: '#fff' }} stroke={2.2} />
                </div>
                <div style={{ color: p.muted, fontSize: fs(1.85), fontWeight: 800, lineHeight: 1.15 }}>{label}</div>
            </div>
            {children}
        </div>
    );
}

export default function LandscapeCardsDesign({ job, business, theme, showFooter = true, className = '' }) {
    const { t } = useTranslation();
    const p = theme.palette;
    const f = posterFields(job, business, t);
    const eduScale = valueFitScale(f.edu, 40);
    const ageScale = valueFitScale(f.age, 24);
    const [c1, c2, c3, c4] = p.cards;

    const body = (
        <div className="flex flex-1 flex-col">
            <div className="relative" style={{ background: `linear-gradient(110deg, ${p.hero}, ${p.heroDeep})`, padding: `${fs(3)} 4.6cqw ${fs(5.4)}` }}>
                <div className="flex items-center" style={{ gap: fs(2.6) }}>
                    <PosterLogo job={job} size={11} style={{ background: '#fff', borderRadius: '50%', padding: fs(1), boxShadow: '0 0.6cqw 1.4cqw rgba(0,0,0,0.25)' }} />
                    <div className="min-w-0 flex-1">
                        <div style={{ color: '#fff', fontSize: fs(4.4), fontWeight: 800, lineHeight: 1.12 }}>{f.org}</div>
                        {f.position && <div style={{ color: '#fff', opacity: 0.9, fontSize: fs(2.3), fontWeight: 600, lineHeight: 1.25, marginTop: fs(0.6) }}>{f.position}</div>}
                    </div>
                    <div className="flex flex-none items-center" style={{ background: 'rgba(255,255,255,0.18)', border: '0.2cqw solid rgba(255,255,255,0.6)', color: '#fff', borderRadius: '99px', padding: `${fs(0.7)} ${fs(2)}`, gap: fs(0.9), fontSize: fs(2), fontWeight: 800 }}>
                        <IconSpeakerphone style={{ width: fs(2.4), height: fs(2.4) }} stroke={2.2} />
                        <div>{f.label.notice}</div>
                    </div>
                </div>
                <svg viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true" style={{ position: 'absolute', left: 0, bottom: '-0.2cqw', width: '100%', height: '3.2cqw' }}>
                    <polygon points="0,10 100,0 100,10" fill={p.bg} />
                </svg>
            </div>

            <div className="relative flex flex-1 flex-col justify-center" style={{ zIndex: 1, padding: `0 4.6cqw ${fs(2)}`, gap: fs(2), marginTop: fs(-2) }}>
                <div className="grid" style={{ gridTemplateColumns: '0.9fr 1.35fr 1fr 1.15fr', gap: fs(1.8) }}>
                    <Card p={p} color={c1} Icon={IconUsersGroup} label={f.label.vacancy}>
                        <div className="flex items-baseline" style={{ gap: fs(0.9) }}>
                            <div style={{ color: c1, fontSize: `calc(${8.4 * numberScale(f.vacancy)}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.05 }}>{f.vacancy}</div>
                            <div style={{ color: p.ink, fontSize: fs(2.4), fontWeight: 800 }}>{f.label.unit}</div>
                        </div>
                    </Card>
                    <Card p={p} color={c2} Icon={IconBooks} label={f.label.edu}>
                        <div style={{ color: p.ink, fontSize: `calc(${2.5 * eduScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.25 }}>{f.edu}</div>
                    </Card>
                    <Card p={p} color={c3} Icon={IconUser} label={f.label.age}>
                        <div style={{ color: p.ink, fontSize: `calc(${2.5 * ageScale}cqw * var(--fs, 1))`, fontWeight: 800, lineHeight: 1.25 }}>{f.age}</div>
                    </Card>
                    <Card p={p} color={c4} Icon={IconCalendarEvent} label={f.label.start}>
                        <div style={{ color: p.ink, fontSize: fs(2.6), fontWeight: 800, lineHeight: 1.2 }}>{f.start}</div>
                        <div className="flex items-center" style={{ gap: fs(0.8), color: p.hot, fontSize: fs(1.85), fontWeight: 800, marginTop: fs(0.4) }}>
                            <IconClockHour4 style={{ width: fs(2.2), height: fs(2.2) }} stroke={2.4} />
                            <div>{f.label.end}</div>
                        </div>
                        <div style={{ color: p.hot, fontSize: fs(2.6), fontWeight: 900, lineHeight: 1.2 }}>{f.end}</div>
                    </Card>
                </div>

                <div className="flex items-center justify-between" style={{ background: `linear-gradient(90deg, ${p.cta}, ${p.ctaDeep})`, color: '#fff', borderRadius: '99px', padding: `${fs(1.4)} ${fs(3)}`, boxShadow: '0 0.7cqw 1.7cqw rgba(15,23,42,0.22)' }}>
                    <div style={{ fontSize: fs(2.9), fontWeight: 800 }}>{f.label.cta}</div>
                    <IconArrowRight className="flex-none" style={{ width: fs(3.4), height: fs(3.4) }} stroke={3} />
                </div>
            </div>
        </div>
    );

    const footer = showFooter ? (
        <div className="flex items-center justify-between" style={{ margin: `0 4.6cqw ${fs(2.6)}`, background: '#fff', borderRadius: fs(2.2), padding: `${fs(1.4)} ${fs(2.6)}`, gap: `${fs(0.8)} ${fs(2.4)}`, flexWrap: shopIsLong(f.shop) ? 'wrap' : 'nowrap', boxShadow: '0 0.7cqw 1.7cqw rgba(15,23,42,0.12)' }}>
            <div className="flex min-w-0 items-center" style={{ gap: fs(1.6) }}>
                {f.shop.logo && <img src={f.shop.logo} alt="" className="flex-none rounded-full object-contain" style={{ width: fs(5.4), height: fs(5.4), border: `0.2cqw solid ${p.bg}`, padding: '0.4cqw' }} />}
                <div style={{ color: p.hero, fontSize: fs(3.2), fontWeight: 800, lineHeight: 1.12, opacity: f.shop.placeholder ? 0.6 : 1 }}>{f.shop.nameShown}</div>
            </div>
            <div className="flex min-w-0 items-center justify-center" style={{ flex: shopIsLong(f.shop) ? '1 0 100%' : '1 1 0%', order: shopIsLong(f.shop) ? 3 : 0, gap: fs(0.8), color: p.ink, fontSize: fs(2.1), lineHeight: 1.25, opacity: f.shop.addressPlaceholder ? 0.6 : 0.9 }}>
                <IconMapPin className="flex-none" style={{ width: fs(2.4), height: fs(2.4), color: p.hot }} />
                <div>{f.shop.addressShown}</div>
            </div>
            {f.shop.phone && (
                <div className="flex flex-none items-center" style={{ gap: fs(0.8), color: p.ink, fontSize: fs(2.9), fontWeight: 800 }}>
                    <IconPhone className="flex-none" style={{ width: fs(2.7), height: fs(2.7), color: p.cta }} />
                    <div>{f.shop.phone}</div>
                </div>
            )}
        </div>
    ) : null;

    return <PosterSheet landscape deps={[job, business, theme, showFooter, t]} className={className} fontFamily={FONT} background={p.bg} body={body} footer={footer} />;
}
